/**
 * Entity extraction utilities for booking data.
 *
 * Provides functions to extract unique employees, customers, and projects
 * from arrays of bookings or booking cards.
 *
 * Single source of truth for entity extraction logic.
 */

import type {
  IServiceEmployee,
  IServiceCustomer,
  IServiceProject,
} from "@/services/types";

/**
 * Generic type for any object that may contain an employee reference.
 */
interface HasEmployee {
  employee?: IServiceEmployee | null;
}

/**
 * Generic type for any object that may contain a customer reference.
 */
interface HasCustomer {
  customer?: IServiceCustomer | null;
}

/**
 * Generic type for any object that may contain a project reference.
 */
interface HasProject {
  project?: IServiceProject | null;
}

/**
 * Extract unique employees from an array of items that have an employee property.
 * Works with bookings, booking cards, or any object with an `employee` field.
 *
 * @example
 * // From booking cards
 * const employees = extractUniqueEmployees(bookingCards);
 *
 * // From raw bookings
 * const employees = extractUniqueEmployees(bookings);
 */
export function extractUniqueEmployees<T extends HasEmployee>(
  items: T[],
): IServiceEmployee[] {
  const employeeMap = new Map<number, IServiceEmployee>();
  for (const item of items) {
    if (item.employee && !employeeMap.has(item.employee.id)) {
      employeeMap.set(item.employee.id, item.employee);
    }
  }
  return Array.from(employeeMap.values());
}

/**
 * Extract unique customers from an array of items that have a customer property.
 *
 * @example
 * const customers = extractUniqueCustomers(bookings);
 */
export function extractUniqueCustomers<T extends HasCustomer>(
  items: T[],
): IServiceCustomer[] {
  const customerMap = new Map<number, IServiceCustomer>();
  for (const item of items) {
    if (item.customer && !customerMap.has(item.customer.id)) {
      customerMap.set(item.customer.id, item.customer);
    }
  }
  return Array.from(customerMap.values());
}

/**
 * Extract unique projects from an array of items that have a project property.
 *
 * @example
 * const projects = extractUniqueProjects(bookings);
 */
export function extractUniqueProjects<T extends HasProject>(
  items: T[],
): IServiceProject[] {
  const projectMap = new Map<number, IServiceProject>();
  for (const item of items) {
    if (item.project && !projectMap.has(item.project.id)) {
      projectMap.set(item.project.id, item.project);
    }
  }
  return Array.from(projectMap.values());
}
